import type { ProductCard } from "@ellaandil/formatters";
import type { ItemList, ListItem, WithContext } from "schema-dts";
import { productCanonicalPath, withDomain } from "@/i18n/routing";
import { safeStringify } from "@/lib/utils/safeStringify";

export interface ProductListingSchemaProps {
	products: ProductCard[];
	language: string;
	country: string;
	slug: string[];
	name?: string;
}

const buildSchema = ({
	products,
	language,
	country,
	slug,
	name,
}: ProductListingSchemaProps): WithContext<ItemList> | null => {
	if (products.length === 0) return null;

	const locale = `${language}-${country}`;

	const itemListElement: ListItem[] = [];
	for (let i = 0; i < products.length; i++) {
		const product = products[i];
		if (!product.uri) continue;
		itemListElement.push({
			"@type": "ListItem",
			position: i + 1,
			url: withDomain(locale, productCanonicalPath(product.uri)),
		});
	}

	if (itemListElement.length === 0) return null;

	const schema: WithContext<ItemList> = {
		"@context": "https://schema.org",
		"@type": "ItemList",
		url: withDomain(locale, slug.join("/")),
		itemListElement,
	};

	if (name) schema.name = name;

	return schema;
};

export const ProductListingSchema = (props: ProductListingSchemaProps) => {
	const schema = buildSchema(props);
	if (!schema) return null;

	return (
		<script
			type="application/ld+json"
			// biome-ignore lint/security/noDangerouslySetInnerHtml: JSON-LD requires raw script content; values are typed via schema-dts and escaped via safeStringify
			dangerouslySetInnerHTML={{ __html: safeStringify(schema) }}
		/>
	);
};
